import { get, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";
import instance from "../http-client/client";

export interface Document {
  name: string;
  displayName?: string;
  sizeBytes?: string;
  state?: string;
  createTime?: string;
  updateTime?: string;
}

export interface ListFilesResponse {
  documents: Document[];
  nextPageToken?: string;
}

export interface Pager<T> {
  page: T[];
  pageInfo?: {
    nextPageToken?: string;
  };
  hasNextPage: () => boolean;
  nextPage: () => Promise<T[]>;
}

/**
 * Get files from the File Search store with pagination
 * @param pageSize - Number of items per page
 * @param pageToken - Token for the next page (from previous response)
 */
export const getRAGFiles = async (
  pageSize?: number,
  pageToken?: string
): Promise<Pager<Document>> => {
  const response: ApiResponse<ListFilesResponse> = await get("/rag/files", {
    pageSize,
    pageToken,
  });

  if (!response.success || !response.data) {
    throw new Error('Failed to fetch RAG files');
  }

  const { documents, nextPageToken } = response.data;

  // Create a Pager object compatible with the frontend component
  const pager: Pager<Document> = {
    page: documents,
    pageInfo: {
      nextPageToken,
    },
    hasNextPage: () => !!nextPageToken,
    nextPage: async () => {
      if (!nextPageToken) {
        return [];
      }
      const nextResponse = await getRAGFiles(pageSize, nextPageToken);
      return nextResponse.page;
    },
  };

  return pager;
};

/**
 * Upload a file to the File Search store
 * @param file - The file to upload
 * @param onProgress - Optional callback for progress updates
 */
export const uploadRAGFile = async (
  file: File,
  onProgress?: (status: string) => void
): Promise<void> => {
  try {
    onProgress?.('Uploading file...');

    const formData = new FormData();
    formData.append('file', file);

    // Upload the file to the backend
    const response = await instance.post('/rag/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const operationName = response.data.data?.operationName;

    if (!operationName) {
      throw new Error('No operation name returned from upload');
    }

    // Poll the operation until it's complete
    onProgress?.('Processing file...');
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (60 * 5 seconds)
    let done = false;
    let hasError = false;

    while (!done && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 5000));

      const statusResponse = await instance.get(`/rag/operations/${encodeURIComponent(operationName)}`);
      const status = statusResponse.data.data;
      attempts++;

      done = status.done;
      hasError = !!status.error;

      if (!done && attempts % 6 === 0) {
        onProgress?.(`Still processing... (${Math.floor(attempts * 5 / 60)} min)`);
      }
    }

    if (!done) {
      throw new Error('Upload operation timed out');
    }

    if (hasError) {
      throw new Error('Upload failed');
    }

    onProgress?.('Upload complete!');
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Delete a file from the File Search store
 */
export const deleteRAGFile = async (name: string): Promise<void> => {
  const response: ApiResponse<void> = await remove(`/rag/files/${encodeURIComponent(name)}`, null);

  if (!response.success) {
    throw new Error('Failed to delete file');
  }
};

